
import { Component, Vue, Watch, Prop, Provide } from "vue-property-decorator"
import { namespace, Getter, Mutation, Action, State } from "vuex-class"

import Guid from "@/scripts/guid"

// @ts-ignore
import VueGridLayout from "@sureview/vue-grid"
import vSelect from "vue-select"

import { Report, ReportDetails, ReportSection, ScheduledReport } from "@/types/report"
import { Schedule, ScheduleFreq } from "@/types/schedule"
import { Insight } from "@/types/insights"
import { TimeZone } from "@/types"
import { Folder } from "@/types/folder"

import Graph from "@/components/Dashboard/Graph.vue"
import ScheduleControl from "@/components/Controls/Schedule.vue"
import SortTabs from "@/components/Controls//Tabs/SortTabs.vue"
import SortTab from "@/components/Controls//Tabs/SortTab.vue"
import DataForm from "../DataForm.vue"
import ReportSectionLayout from "./ReportSectionLayout.vue"
import { TRANSITION_FOCUS_DELAY_MS } from "@/constants/animations"
import ScheduleEditService from "@/services/scheduleEditService"
import { TenantFeatures } from "@/types/tenantFeatures"
import { CurrentUser } from "@/types/user"
import RecipientList from "../Rules/RecipientList.vue"
import TextAreaToggleExpand from "@/components/Fields/TextAreaToggleExpand.vue"

@Component({
	components: {
		"grid-layout": VueGridLayout.GridLayout,
		"grid-item": VueGridLayout.GridItem,
		"graph": Graph,
		"v-select": vSelect,
		"schedule": ScheduleControl,
		"data-form": DataForm,
		"report-section-layout": ReportSectionLayout,
		"sort-tabs": SortTabs,
		"sort-tab": SortTab,
		"recipient-list": RecipientList
	}
})
export default class ReportEditor extends Vue {
	@Provide()
	noPopup = true

	// --- VueX ---
	@State tenantSettings!: { [key: string]: any } | null

	@Getter("getMe") public me!: CurrentUser;

	@Getter("getTenantTimezones") tenantTimezones!: TimeZone[]

	@Getter("getRecordName") getRecordName!: string;

	@Getter getIsFeatureEnabled!: (feature: TenantFeatures) => boolean

	// --- Props ---

	@Prop({ required: true })
	value!: ScheduledReport

	@Prop()
	insights!: Insight[]

	@Prop()
	folders!: Folder[]

	// --- Data ---

	selectedTab: string | null = "settings"

	private scheduleEditService = new ScheduleEditService()

	// --- Computed Properties ---

	private scheduleColumnIds: string[] = ["scheduleFreq", "excludeWeekends", "month", "dayOf", "date", "dates", "minRunDate", "maxRunDate"]

	public get scheduleColumns () {
		if (!this.scheduleEditService || !this.value.scheduled) return []

		if (this.scheduleEditService.scheduleDisplayRunTimes) {
			this.scheduleColumnIds = [...this.scheduleColumnIds, "nextRunTimes"]
		}

		if (!this.scheduleEditService.scheduleHideStartTimeFeatureEnabled) {
			this.scheduleColumnIds = [...this.scheduleColumnIds, "startTime", "recordsStartTimeWarningSpecified"]
		} else {
			this.scheduleColumnIds = [...this.scheduleColumnIds, "recordsStartTimeWarning"]
		}

		const columns = this.scheduleEditService.columns.map((column: any) =>
			(["recordsStartTimeWarningSpecified", "recordsStartTimeWarning"].includes(column.key))
				? { ...column, label: column.label.replace("services", "reports") }
				: column
		)

		return columns.filter((c: any) => this.scheduleColumnIds.find(s => s === c.key))
	}

	public get settingsColumns () {
		return [
			{
				key: "timeZone",
				label: "Timezone",
				dataType: "list",
				options: this.tenantTimezones,
				description: "This will set the timezone for how times and dates in the report are displayed. For insights without a timezone set, it will also use this for any relative time filters.",
				errors: (item: ScheduledReport) => ((item.scheduled && !item.timeZone) ? "Select a time zone for Scheduling" : null)
			},
			{
				key: "folder",
				label: "Folder",
				dataType: "list",
				options: this.folders,
				sortable: true,
				inTable: false
			},
			{
				key: "scheduled",
				label: "Scheduled",
				dataType: "checkbox",
				inModal: (item: ScheduledReport) => (item.scheduled || !!this.getIsFeatureEnabled(TenantFeatures.ReportScheduledEmails)),
				sortable: true,
				inTable: true,
				description: "Send Reports to recipients on a Schedule."
			},
			{
				key: "recipients",
				label: "Email Recipients",
				sortable: true,
				dataType: "date",
				dynamicComponent: RecipientList,
				inModal: (item: ScheduledReport) => (item.scheduled),
				componentProperties: {
					"v-model": this.value.recipients,
					allowEmailAddress: true,
					dropDirection: "right"
				},
				inTable: false
			},
			{
				key: "emailBody",
				label: "Email Body",
				sortable: true,
				dataType: "date",
				dynamicComponent: TextAreaToggleExpand,
				inModal: (item: ScheduledReport) => (item.scheduled),
				componentProperties: {
					allowRichText: true,
					allowImages: false
				},
				inTable: false
			}
		]
	}

	get sortedTabKeys (): string[] {
		return this.value.sections.map(s => s.id)
	}
	set sortedTabKeys (sectionIds: string[]) {
		this.value.sections = this.value.sections.sortByMap(s => sectionIds.indexOf(s.id))
		this.$emit("input", this.value)
	}

	// --- Lifecycle Hooks ---

	public async mounted () {
		await this.scheduleEditService.load(this.me, true)

		if (this.scheduleEditService) {
			var newSchedule: Schedule = {
				timeZoneId: "",
				fields: {},
				templateId: null,
				tenantId: "",
				title: "",
				startTime: null,
				endTime: null,
				templateTitle: "",
				fieldLabels: {}
			}
			this.value.schedule = this.value.schedule ?? newSchedule
			this.scheduleEditService.currentSchedule = this.value.schedule
			if (this.value.timeZone) {
				this.scheduleEditService.currentSchedule.timeZoneId = this.value.timeZone
			}
			this.initializeScheduleEditService()
			this.scheduleEditService.timeZoneChanged(this.scheduleEditService.currentSchedule.timeZoneId)
		}

		if (this.getIsFeatureEnabled(TenantFeatures.ReportScheduledEmailsIncludeTime)) {
			this.scheduleEditService.scheduleHideStartTimeFeatureEnabled = false
		}

		if (this.value) {
			if (this.value.sections.length === 0) {
				this.addSection()
			} else {
				this.selectedTab = this.value.sections[0].id
			}
		}
	}
	// --- Event Handlers ---

	/**
	 * @summary Update the layout of a section.
	 * @param {ReportSection} updatedSection - updated values for section
	 */
	public updateSection (updatedSection: ReportSection) {
		const sectionIndex = this.value.sections.findIndex(s => s.id === updatedSection.id)
		if (sectionIndex > -1) {
			Vue.set(this.value.sections, sectionIndex, {
				...this.value.sections[sectionIndex],
				...updatedSection
			})
			this.$emit("input", this.value)
		}
	}

	/**
	 * @summary Add an additional summary, then select that tab
	 */
	private addSection () {
		const newTabId = Guid.newGuid()
		this.value.sections.push({
			id: newTabId,
			title: "New Section",
			columns: 2,
			contents: []
		})
		this.$emit("input", this.value)

		setTimeout(() => {
			this.selectedTab = newTabId
			setTimeout(() => {
				const titleTextBox = this.$el.querySelector(".section-title") as HTMLInputElement
				if (titleTextBox) {
					titleTextBox.focus()
				}
			}, TRANSITION_FOCUS_DELAY_MS)
		}, TRANSITION_FOCUS_DELAY_MS)
	}

	/**
	 * @summary Remove section from value
	 */
	private removeSection (section: ReportSection) {
		this.value.sections = this.value.sections.filter(s => s.id !== section.id)
		this.$emit("input", this.value)
	}

	// --- Scheduling ---

	private initializeScheduleEditService () {
		// data
		this.scheduleEditService.recordName = this.getRecordName
		this.scheduleEditService.tenantTimeZones = this.tenantTimezones

		// tenant features
		this.scheduleEditService.getIsFeatureEnabled = this.getIsFeatureEnabled
		this.scheduleEditService.initializeFeatures()
	}

	@Watch("value.timeZone")
	private timeZoneChanged (selectedTimeZone: string) {
		this.scheduleEditService.timeZoneChanged(selectedTimeZone)
	}

	@Watch("scheduleEditService.currentSchedule.minRunDate")
	private minRunDateChanged (selectedDate: Date) {
		this.scheduleEditService.minRunDateChanged(selectedDate)
	}

	@Watch("scheduleEditService.currentSchedule.maxRunDate")
	private maxRunDateChanged (selectedDate: Date) {
		this.scheduleEditService.maxRunDateChanged(selectedDate)
	}

	@Watch("scheduleEditService.currentSchedule.scheduleFreq")
	private scheduleFrequencyChanged (scheduleFrequency: any) {
		this.scheduleEditService.scheduleFrequencyChanged(scheduleFrequency)
	}

	@Watch("scheduleEditService.currentSchedule.startTime")
	private startTimeChanged (startTime: string) {
		this.scheduleEditService.startTimeChanged(startTime)
	}
}

